import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Edit, Trash2, FileSpreadsheet, FileText, Play, Calendar } from 'lucide-react';
import { Share2 } from 'lucide-react';
import useParams from '../hooks/useParams';
import { downloadFromFetch } from '../utils/downloadFile';
import { apiFetch } from '../utils/api';
import LogSessionModal from '../components/LogSessionModal';
import { useTranslation } from 'react-i18next';
import './WorkoutDetailPage.css';

const parseCSV = (str) => String(str || '').split(',').map(v => v.trim()).filter(Boolean);

const WorkoutDetailPage = () => {
  const [workout, setWorkout] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  useEffect(() => {
    fetchWorkout();
  }, [workoutId]);

  const fetchWorkout = async () => {
    try {
      const [workoutRes, sessionsRes] = await Promise.all([
        apiFetch(`/api/workouts/${workoutId}`),
        apiFetch(`/api/sessions?workout_id=${workoutId}`)
      ]);
      if (workoutRes.ok) setWorkout(await workoutRes.json());
      if (sessionsRes.ok) setSessions(await sessionsRes.json());
    } catch (error) {
      console.error('Error fetching workout:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('workout_detail.delete_confirm'))) return;
    try {
      const response = await apiFetch(`/api/workouts/${workoutId}`, { method: 'DELETE' });
      if (response.ok) navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  const handleExport = (format) => {
    const extension = format === 'excel' ? 'xlsx' : 'pdf';
    const filename = `DragonFit_${workout.name}.${extension}`;
    downloadFromFetch(async () => {
      const response = await apiFetch(`/api/export/${format}/${workoutId}`);
      return response.ok ? response.blob() : null;
    }, filename);
  };

  const handleShare = async () => {
    try {
      const res = await apiFetch(`/api/workouts/${workoutId}/share`, { method: 'POST' });
      if (!res.ok) return;
      const data = await res.json();
      const url = data.share_url;
      if (navigator.share) {
        navigator.share({ title: 'DragonFit Routine', text: t('workout_detail.share_text'), url });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(url)}`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleStartSession = () => {
    navigate(`/workout/${workout.workout_id}/log/${activeDay}`);
  };

  // Total volume lifted in a session (sum of weight × reps across all exercises/sets)
  const getSessionVolume = (session) => {
    if (!session?.exercises) return 0;
    return session.exercises.reduce((total, ex) => {
      const weights = parseCSV(ex.weight).map(Number);
      const repsList = parseCSV(ex.reps).map(Number);
      return total + weights.reduce((s, w, i) => {
        const r = repsList[i] ?? 0;
        return isNaN(w) || isNaN(r) ? s : s + w * r;
      }, 0);
    }, 0);
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(dateLocale, {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const s = new Date(dateStr + 'T12:00:00').toLocaleDateString(dateLocale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner" /></div>;
  }

  if (!workout) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <h3 className="empty-title">{t('workout_detail.not_found')}</h3>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>{t('common.back')}</button>
        </div>
      </div>
    );
  }

  const currentDay = workout.days?.[activeDay];

  // Most recent session for the active day (API returns sessions sorted newest first)
  const lastSessionForDay = sessions.find(s => s.day_index === activeDay) || null;
  const lastSessionVolume = lastSessionForDay ? getSessionVolume(lastSessionForDay) : 0;

  return (
    <div className="page-container animate-fade-in" data-testid="workout-detail-page">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="header">
        <div>
          <button className="btn btn-sm btn-secondary mb-2" onClick={() => navigate('/dashboard')}>
            {t('common.back')}
          </button>
          <h1 className="header-title">{workout.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(`/workout/${workout.workout_id}/edit`)}
            data-testid="edit-workout-btn"
          >
            <Edit size={18} />
          </button>
          <button className="btn btn-icon btn-secondary" onClick={handleShare}>
            <Share2 size={18} />
          </button>
          <button
            className="btn btn-icon btn-danger"
            onClick={handleDelete}
            data-testid="delete-workout-btn"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {workout.description && (
        <p className="text-muted mb-4">{workout.description}</p>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button className="btn btn-sm btn-outline" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
          <FileSpreadsheet size={16} /> Excel
        </button>
        <button className="btn btn-sm btn-outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
          <FileText size={16} /> PDF
        </button>
      </div>

      {/* ── Day tabs ─────────────────────────────────────────── */}
      <div className="day-tabs">
        {workout.days?.map((day, index) => (
          <button
            key={index}
            className={`day-tab ${activeDay === index ? 'active' : ''}`}
            onClick={() => setActiveDay(index)}
            data-testid={`day-tab-${index}`}
          >
            {day.name}
          </button>
        ))}
      </div>

      {/* ── Last-session banner ───────────────────────────────── */}
      {lastSessionForDay && (
        <div className="wd-session-banner">
          <Calendar size={14} className="wd-session-banner-icon" />
          <div className="wd-session-banner-info">
            <span className="wd-session-banner-label">Última sesión</span>
            <span className="wd-session-banner-date">{formatDateLong(lastSessionForDay.date)}</span>
          </div>
          {lastSessionVolume > 0 && (
            <span className="wd-session-banner-vol">
              {Math.round(lastSessionVolume).toLocaleString()}kg
            </span>
          )}
        </div>
      )}

      {/* ── Start session button ─────────────────────────────── */}
      <button
        className="btn btn-primary wd-start-btn"
        onClick={() => setShowStartModal(true)}
      >
        <Play size={18} />
        {t('workout_detail.log_session')}
      </button>

      {/* ── Exercise cards ────────────────────────────────────── */}
      {currentDay?.exercises?.length > 0 ? (
        currentDay.exercises.map((exercise, index) => {
          const exData = lastSessionForDay?.exercises?.find(e => e.exercise_index === index);
          const weights = exData ? parseCSV(exData.weight) : [];
          const reps    = exData ? parseCSV(exData.reps)   : [];
          const maxSets = Math.max(weights.length, reps.length);

          const numWeights = weights.map(Number).filter(n => !isNaN(n) && n > 0);
          const numReps    = reps.map(Number).filter(n => !isNaN(n) && n > 0);
          const maxWeight  = numWeights.length > 0 ? Math.max(...numWeights) : 0;
          const totalVolume = numWeights.reduce((sum, w, i) => sum + w * (numReps[i] ?? 0), 0);

          return (
            <div
              key={index}
              className={`wd-exercise-card${exData ? ' wd-has-data' : ''}`}
              data-testid={`exercise-item-${index}`}
            >
              {/* Header: name + goal */}
              <div className="wd-exercise-header">
                <p className="wd-exercise-name">{exercise.name}</p>
                {exercise.sets && (
                  <span className="wd-exercise-goal">{exercise.sets}</span>
                )}
              </div>

              {maxSets > 0 ? (
                <>
                  {/* Per-set rows */}
                  <div className="wd-sets-list">
                    {Array.from({ length: maxSets }, (_, sIdx) => {
                      const w = weights[sIdx];
                      const r = reps[sIdx];
                      return (
                        <div key={sIdx} className="wd-set-row">
                          <span className="wd-set-num">S{sIdx + 1}</span>

                          <div className="wd-set-weight-cell">
                            {w ? (
                              <>
                                <span className="wd-set-value-weight">{w}</span>
                                <small className="wd-set-unit">kg</small>
                              </>
                            ) : (
                              <span className="wd-set-dash">—</span>
                            )}
                          </div>

                          <span className="wd-set-sep">×</span>

                          <div className="wd-set-reps-cell">
                            {r ? (
                              <>
                                <span className="wd-set-value-reps">{r}</span>
                                <small className="wd-set-unit">reps</small>
                              </>
                            ) : (
                              <span className="wd-set-dash">—</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Stats: best weight + volume */}
                  {(maxWeight > 0 || totalVolume > 0) && (
                    <div className="wd-exercise-footer">
                      {maxWeight > 0 && (
                        <span className="wd-stat-chip wd-stat-best">
                          🏋️ {maxWeight}kg max
                        </span>
                      )}
                      {totalVolume > 0 && (
                        <span className="wd-stat-chip wd-stat-vol">
                          ⚡ {Math.round(totalVolume).toLocaleString()}kg vol.
                        </span>
                      )}
                    </div>
                  )}

                  {/* Session notes for this exercise */}
                  {exData?.notes && (
                    <p className="wd-exercise-notes">"{exData.notes}"</p>
                  )}
                </>
              ) : (
                /* No session data yet */
                <div className="wd-no-data">
                  {exercise.notes ? (
                    <p className="wd-plan-notes">{exercise.notes}</p>
                  ) : (
                    <p className="wd-no-session-text">Sin registro todavía · Empieza el primer entrenamiento</p>
                  )}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="empty-state">
          <p className="text-muted">{t('workout_detail.no_exercises')}</p>
        </div>
      )}

      {/* ── Recent sessions (filtered by active day) ─────────── */}
      {(() => {
        const sessionsForDay = sessions.filter(s => s.day_index === activeDay);
        if (sessionsForDay.length === 0) return null;
        return (
        <div className="mt-6">
          <p className="wd-sessions-title">{t('workout_detail.recent_sessions')}</p>
          {sessionsForDay.slice(0, 5).map((session) => {
            const vol = getSessionVolume(session);
            return (
              <div
                key={session.session_id}
                className="wd-session-item"
                onClick={() => navigate(`/session/${session.session_id}`)}
              >
                <div className="wd-session-item-main">
                  <span className="wd-session-item-day">{session.day_name}</span>
                  <span className="wd-session-item-date">{formatDateShort(session.date)}</span>
                </div>
                <div className="wd-session-item-meta">
                  <span className="wd-session-item-count">
                    {t('workout_detail.exercises_count', { count: session.exercises?.length || 0 })}
                  </span>
                  {vol > 0 && (
                    <span className="wd-session-item-vol">
                      {Math.round(vol).toLocaleString()}kg vol.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* ── Modals ───────────────────────────────────────────── */}
      {showLogModal && (
        <LogSessionModal
          workout={workout}
          dayIndex={activeDay}
          onClose={() => setShowLogModal(false)}
          onSaved={() => { setShowLogModal(false); fetchWorkout(); }}
        />
      )}

      {showStartModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowStartModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '32px' }}>🐉</div>
            <h2 style={{ textAlign: 'center', marginBottom: '8px' }}>{t('workout_detail.ready_title')}</h2>
            <p style={{ textAlign: 'center', fontSize: '14px', color: 'var(--muted-foreground)', marginBottom: '24px' }}>
              {t('workout_detail.ready_desc')}{' '}
              <strong style={{ color: 'var(--foreground)' }}>{currentDay?.name}</strong>
              {' '}{t('workout_detail.ready_of')}{' '}
              <strong style={{ color: 'var(--foreground)' }}>{workout.name}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                className="btn btn-primary w-full"
                onClick={() => { setShowStartModal(false); handleStartSession(); }}
              >
                <Play size={18} />
                {t('workout_detail.yes_start')}
              </button>
              <button className="btn btn-secondary w-full" onClick={() => setShowStartModal(false)}>
                {t('workout_detail.not_now')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WorkoutDetailPage;
